import { Address, UserInfo } from 'types';
import { Button } from 'primereact/button';
import { CustomerOption } from 'components/Checkout';
import { itemsAreAllVirtual } from 'utils/cartUtils';
import { Messages } from 'primereact/messages';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { showErrorMessage } from 'utils/errorHelpers';
import { useCartContext } from 'CartContext';
import { useForm } from 'react-hook-form';
import { useRegisterAndLogin, useToken } from 'hooks/userHooks';
import { useSetBillingAddress } from 'hooks/magentoHooks';
import AddressFormInput from 'components/forms/AddressFormInput';
import FormInput from 'components/forms/FormInput';
import PasswordInput from 'components/forms/PasswordInput';
import ReactGA from 'react-ga4';
import { useAuth0 } from '@auth0/auth0-react';

type FieldValuesType = Address & {
  email: string;
  password: string;
  confirmPassword: string;
};

export interface AccountAndBillingFormProps {
  newCustomerOption: CustomerOption;
  setHasConfirmedBilling: (hasConfirmed: boolean) => void;
}

export default function AccountAndBillingForm(
  props: AccountAndBillingFormProps,
): ReactElement {
  const { newCustomerOption, setHasConfirmedBilling } = props;
  const {
    items,
    billingAddress,
    userInfo,
    setBillingAddress,
    setLoggedInUserId,
    setUserInfo,
  } = useCartContext();
  const shouldDisplayCountryField = itemsAreAllVirtual(items);

  const { user } = useAuth0();

  const { setToken } = useToken();

  const [defaultValues] = useState<FieldValuesType>({
    address1: billingAddress?.address1 || '',
    address2: billingAddress?.address2 || '',
    city: billingAddress?.city || '',
    confirmPassword: '',
    country: shouldDisplayCountryField
      ? billingAddress?.country || { name: '', iso3: '', code: '' }
      : { name: 'United States', iso3: 'USA', code: 'US' },
    email: userInfo?.email || user?.email || '',
    firstName: userInfo?.firstName || '',
    lastName: userInfo?.lastName || '',
    mobile: userInfo?.mobile || '',
    password: '',
    postalCode: billingAddress?.postalCode || '',
    stateRegion: billingAddress?.stateRegion || { name: '', code: '' },
  });

  const messages = useRef<Messages>(null);

  const { control, formState, handleSubmit, watch, setError } =
    useForm<FieldValuesType>({
      defaultValues,
    });

  const { errors } = formState;

  const [billingAddressLocal, setBillingAddressLocal] = useState<
    Address | undefined
  >();
  const [addressMutationState, setMagentoBillingAddress] =
    useSetBillingAddress();
  const [userMutationState, registerAndLogin] = useRegisterAndLogin();

  const submitting =
    addressMutationState.fetching || userMutationState.fetching;

  useEffect(() => {
    if (
      addressMutationState.data &&
      !addressMutationState.error &&
      billingAddressLocal
    ) {
      setBillingAddress(billingAddressLocal);
      setHasConfirmedBilling(true);
    }
    if (addressMutationState.error) {
      showErrorMessage(messages.current, addressMutationState.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressMutationState, billingAddressLocal]);

  useEffect(() => {
    if (
      newCustomerOption === CustomerOption.registerAccount &&
      userMutationState.data &&
      !userMutationState.error
    ) {
      const { token, userId } = userMutationState.data.registerAndLoginUser;
      setToken(token);
      setLoggedInUserId(userId);
    }
    if (userMutationState.error) {
      console.log(userMutationState.error);
      showErrorMessage(messages.current, userMutationState.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userMutationState, newCustomerOption]);

  const onSubmit = (data: FieldValuesType) => {
    messages.current?.clear();

    const {
      address1,
      address2,
      city,
      country,
      email,
      firstName,
      lastName,
      mobile,
      password,
      postalCode,
      stateRegion,
    } = data;

    if (!country?.code) {
      setError('country', {
        type: 'required',
        message: 'Country is required.',
      });
      return;
    }

    const address: Address = {
      address1,
      address2,
      city,
      country,
      firstName,
      lastName,
      mobile,
      postalCode,
      stateRegion,
    };

    const formUser: UserInfo = {
      address,
      email,
      firstName,
      lastName,
      mobile,
    };

    setHasConfirmedBilling(false);
    setMagentoBillingAddress(address, email);
    setBillingAddressLocal(address);
    setUserInfo(formUser);

    if (newCustomerOption === CustomerOption.registerAccount) {
      registerAndLogin(formUser, password);
    }

    ReactGA.event({
      category: 'ecommerce',
      action: 'Checkout_Step_2',
      label: 'Checkout Step 2 - Account Details Complete',
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
      <div className="grid">
        <div className="col-12">
          <h2 className="form-heading">Your Personal Details</h2>
          <div className="grid">
            <div className="col-12 md:col-6">
              <FormInput
                fieldName="firstName"
                label="First Name"
                rules={{ required: 'First name is required.' }}
                errors={errors}
                control={control}
              />
            </div>
            <div className="col-12 md:col-6">
              <FormInput
                fieldName="lastName"
                label="Last Name"
                rules={{ required: 'Last name is required.' }}
                errors={errors}
                control={control}
              />
            </div>
          </div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <FormInput
                fieldName="email"
                label="Email"
                rules={{ required: 'Email is required.' }}
                errors={errors}
                control={control}
                defaultValue={user?.email || ''}
              />
            </div>
            <div className="col-12 md:col-6">
              <FormInput
                fieldName="mobile"
                label="Mobile"
                rules={{ required: 'Mobile phone number is required.' }}
                errors={errors}
                control={control}
              />
            </div>
          </div>

          {newCustomerOption === CustomerOption.registerAccount && (
            <>
              <h2 className="form-heading">Password</h2>
              <PasswordInput
                fieldName="password"
                confirmFieldName="confirmPassword"
                errors={errors}
                control={control}
                watch={watch}
              />
            </>
          )}
        </div>

        <div className="col-12">
          <h2 className="form-heading">Your Billing Address</h2>
          <AddressFormInput control={control} errors={errors} />
        </div>
        <Messages ref={messages} />
        <Button
          className="uppercase border-noround c-button-primary"
          type="submit"
          label="Continue"
          loading={submitting}
          id="checkout-billing"
        />
      </div>
    </form>
  );
}
